/**
 * The card detail panel: read-only (spec 2026-10-28, "Client Work view shows
 * the portal lifecycle read-only"). Title, the briefing/script, the phase,
 * the editor's first name (never the payout or their email), the deadline,
 * and the delivery link once delivered. No field is editable and there is no
 * comment box - a client comments on their Trello card directly (routed to the
 * editor by the n8n comment ping) and gives revision feedback in FreeFrame.
 *
 * @module web/screens/app/board/CardPanel
 */
import { useEffect, useRef, useState } from "react";
import type { ClientWorkItem, WorkPhaseStep } from "../../../api/types.ts";
import { avatarColor, avatarInitial, formatDue, timeAgo } from "./cards.ts";
import { PHASE_LABELS } from "./phases.ts";
import { stepLabel } from "./steps.ts";

/** Matches the panel's slide-out transition, so it unmounts after it finishes. */
const EXIT_MS = 300;

export interface CardPanelProps {
  /** The open item, or null when the panel is closed. */
  item: ClientWorkItem | null;
  onClose: () => void;
}

export function CardPanel({ item, onClose }: CardPanelProps) {
  // What to draw. `item` goes null the moment it closes, but the panel has to
  // stay on screen for the slide-out, so the last item is held until then.
  const [ghost, setGhost] = useState<ClientWorkItem | null>(null);
  const [open, setOpen] = useState(false);
  const closeRef = useRef<HTMLButtonElement>(null);

  useEffect(() => { if (item) setGhost(item); }, [item]);
  useEffect(() => {
    if (item === null) {
      setOpen(false);
      const t = setTimeout(() => setGhost(null), EXIT_MS);
      return () => clearTimeout(t);
    }
    const raf = requestAnimationFrame(() => requestAnimationFrame(() => setOpen(true)));
    return () => cancelAnimationFrame(raf);
  }, [item]);

  useEffect(() => { if (open) closeRef.current?.focus(); }, [open]);

  const shown = item ?? ghost;
  if (!shown) return null;

  const due = formatDue(shown.deadlineAt, shown.phase);
  // Delivered AND In revision both carry a reopen-able FreeFrame link (spec
  // 2026-10-29) - a revision round never takes away the link a client already
  // has, it just also shows the "In revision" badge above it.
  const delivered = (shown.phase === "delivered" || shown.phase === "in_revision")
    && shown.deliveredUrl && /^https:\/\//i.test(shown.deliveredUrl)
    ? shown.deliveredUrl
    : null;

  return (
    <>
      <div className={`card-panel-overlay${open ? " open" : ""}`} onClick={onClose} aria-hidden="true" />
      <section
        className={`card-panel${open ? " open" : ""}`}
        id="card-panel"
        role="dialog"
        aria-modal="true"
        aria-label="Card details"
      >
        <header className="panel-header">
          <div style={{ flex: 1, minWidth: 0 }}>
            <h2 className="panel-title" id="panel-card-title">{shown.title || "Untitled"}</h2>
            <div style={{ marginTop: 6 }} id="panel-card-phase">
              <span className="panel-badge">{PHASE_LABELS[shown.phase]}</span>
            </div>
          </div>
          <button className="panel-close" id="panel-close-btn" onClick={onClose} aria-label="Close panel" ref={closeRef}>
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
              <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
            </svg>
          </button>
        </header>

        <div className="panel-body">
          {delivered && (
            <a
              className="btn btn-primary panel-delivery-cta"
              id="panel-delivery-cta"
              href={delivered}
              target="_blank"
              rel="noopener noreferrer"
            >
              View your delivery
            </a>
          )}

          {shown.steps.length > 0 && (
            <div className="panel-field" id="panel-steps-field">
              <label>Timeline</label>
              <StepTimeline steps={shown.steps} currentPhase={shown.phase} />
            </div>
          )}

          {shown.scriptMd && (
            <div className="panel-field" id="panel-script-field">
              <label>Briefing</label>
              <p style={{ whiteSpace: "pre-wrap" }}>{shown.scriptMd}</p>
            </div>
          )}

          <div className="panel-field" id="panel-editor-field">
            <label>Editor</label>
            {shown.editorFirstName ? (
              <div className="panel-assignee">
                <div className="panel-avatar" style={{ background: avatarColor(shown.editorFirstName) }}>
                  {avatarInitial(shown.editorFirstName)}
                </div>
                {shown.editorFirstName}
              </div>
            ) : (
              <p>Not assigned yet</p>
            )}
          </div>

          {due && (
            <div className="panel-field" id="panel-due-field">
              <label>Deadline</label>
              <span className={`panel-badge${due.overdue ? " kc-due overdue" : ""}`}>{due.label}</span>
            </div>
          )}
        </div>
      </section>
    </>
  );
}

/**
 * The phase history (spec #192): a vertical timeline, oldest first, in
 * exactly the order the server sends, using the SAME phase vocabulary as the
 * board columns (`PHASE_LABELS`) - never a second one. A phase re-entered
 * later (a revision round, a give-back) appears again, each its own entry.
 * `editorFirstName` only ever shows up on an `in_production` entry. The last
 * entry always matches the card's current phase, so highlighting it is
 * enough to mark "you are here".
 */
function StepTimeline({ steps, currentPhase }: { steps: WorkPhaseStep[]; currentPhase: string }) {
  return (
    <ol className="step-timeline">
      {steps.map((step, i) => {
        const current = i === steps.length - 1 && step.phase === currentPhase;
        return (
          <li className={`step-timeline-item${current ? " current" : ""}`} key={`${step.phase}-${step.at}`}>
            <span className="step-dot" aria-hidden="true" />
            <div className="step-timeline-body">
              <span className="step-label">
                {stepLabel(step)}
                {step.editorFirstName ? ` · ${step.editorFirstName}` : ""}
              </span>
              <span className="step-at">{timeAgo(step.at)}</span>
            </div>
          </li>
        );
      })}
    </ol>
  );
}
